/**
 * LaTeX to SPINE Conversion Service
 * API client for converting LaTeX expressions to SPINE format
 */

import apiClient from './api';

// ============================================================================
// Types
// ============================================================================

export interface SpineElement {
  id: string;
  type: string;
  order: number;
  [key: string]: any; // Allow additional properties based on type
}

export interface ConvertResponse {
  success: boolean;
  latex: string;
  spine?: SpineElement;
  plainText?: string;
  errors?: string[];
}

export interface ValidateResponse {
  isValid: boolean;
  latex: string;
  errors: string[];
}

export interface BatchConvertResult {
  id?: string;
  latex: string;
  success: boolean;
  errors: string[];
  spine: SpineElement | null;
  plainText: string | null;
}

export interface BatchConvertResponse {
  success: boolean;
  totalCount: number;
  successCount: number;
  failureCount: number;
  results: BatchConvertResult[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Convert a single LaTeX expression to SPINE format
 */
export const convertLatexToSpine = async (latex: string): Promise<ConvertResponse> => {
  try {
    const response = await apiClient.post<ConvertResponse>('/latex-to-spine/convert', { latex });
    return response.data;
  } catch (error: any) {
    // Extract error messages from backend response if available
    if (error.response?.data) {
      const backendResponse = error.response.data;
      return {
        success: false,
        latex,
        errors: backendResponse.errors || [backendResponse.error || error.message],
      };
    }
    // Fallback to generic error
    return {
      success: false,
      latex,
      errors: [error.message || 'Unknown error occurred'],
    };
  }
};

/**
 * Validate a LaTeX expression without converting
 */
export const validateLatex = async (latex: string): Promise<ValidateResponse> => {
  const response = await apiClient.post<ValidateResponse>('/latex-to-spine/validate', { latex });
  return response.data;
};

/**
 * Convert multiple LaTeX expressions to SPINE format
 */
export const batchConvertLatexToSpine = async (
  expressions: Array<{ latex: string; id?: string }>
): Promise<BatchConvertResponse> => {
  const response = await apiClient.post<BatchConvertResponse>('/latex-to-spine/batch', {
    expressions,
  });
  return response.data;
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a SPINE object is empty or invalid
 */
export const isSpineEmpty = (spine: any): boolean => {
  if (!spine) return true;
  if (typeof spine !== 'object') return true;
  if (Array.isArray(spine)) return spine.length === 0;
  return Object.keys(spine).length === 0;
};

/**
 * Get a human-readable summary of a SPINE element
 */
export const getSpineSummary = (spine: SpineElement | null): string => {
  if (!spine) return 'No SPINE data';

  const type = spine.type || 'Unknown';

  switch (type) {
    case 'INTEGER':
      return `Integer: ${spine.value}`;
    case 'DECIMAL':
      return `Decimal`;
    case 'FRACTION':
      return `Fraction`;
    case 'VARIABLE':
      return `Variable: ${spine.value}`;
    case 'COMPOSITE':
      const count = spine.regexTargets?.length || 0;
      return `Composite (${count} elements)`;
    case 'EXPONENT':
      return `Exponent`;
    case 'RADICAL':
      return `Radical`;
    default:
      return type;
  }
};
